import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import { ElMessage, ElMessageBox } from 'element-plus'

// 消息挂载到视口顶部容器，保证提示始终在屏幕顶部可见（不随页面滚动）
const getMessageRoot = () => {
  let el = document.getElementById('app-message-viewport')
  if (!el && typeof document !== 'undefined') {
    el = document.createElement('div')
    el.id = 'app-message-viewport'
    el.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:9999;pointer-events:none;'
    document.body.insertBefore(el, document.body.firstChild)
  }
  return el
}
// 为所有消息提示统一添加关闭按钮、固定在视口顶部
;(['success', 'error', 'warning', 'info']).forEach((type) => {
  const original = ElMessage[type]
  ElMessage[type] = (options) => {
    const opts = typeof options === 'string' ? { message: options } : { ...options }
    return original({
      showClose: true,
      offset: 24,
      appendTo: getMessageRoot(),
      ...opts
    })
  }
})
import 'element-plus/dist/index.css'
import {
  User,
  Medal,
  ChatDotRound,
  Trophy,
  DataLine,
  ShoppingBag,
  Document,
  ChatLineRound,
  Setting,
  SwitchButton,
  ArrowDown,
  Bell,
  MoreFilled,
  Loading,
  Link,
  ArrowUp,
  InfoFilled,
  Clock,
  ArrowLeft,
  ArrowRight,
  Wallet,
  Plus,
  Upload,
  Delete,
  Edit,
  CircleCheck,
  CircleClose,
  StarFilled,
  Picture,
  List,
  PriceTag,
  FolderOpened,
  Operation,
  Collection,
  UploadFilled,
  Check,
  Search,
  Folder,
  Star,
  Monitor,
  Cpu,
  Connection,
  Tools,
  Refresh,
  Warning,
  QuestionFilled,
  Share
} from '@element-plus/icons-vue'
import App from './App.vue'
import router from './router'
import './styles/variables.css'
import './styles/theme.scss'
// 引入新的设计系统样式
import './styles/theme.css'
// 游戏化视觉 token（深色/任务系统风格，覆盖 theme.css 变量）
import './styles/tokens-game.css'
import './styles/cosmetics-tokens.css'
import './styles/app.css'
import './styles/element-plus-overrides.css'
import './styles/light-mode-overrides.css'
import { useAuthStore } from './stores/auth'
import { useThemeStore } from './stores/theme'
import { initTheme } from './composables/useTheme'
import { startChallengesPrefetch } from './utils/challengesPrefetch'
import { startKbPrefetch } from './utils/kbPrefetch'

const app = createApp(App)
const pinia = createPinia()
let chunkErrorPrompting = false

const isChunkLoadError = (message = '') => {
  const msg = String(message || '')
  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  )
}

const promptChunkRefresh = async () => {
  if (chunkErrorPrompting) return
  chunkErrorPrompting = true
  try {
    await ElMessageBox.alert(
      '系统已更新，点击刷新以加载最新版本。',
      '页面需要刷新',
      {
        confirmButtonText: '立即刷新',
        closeOnClickModal: false,
        closeOnPressEscape: false,
        showClose: false,
        type: 'warning'
      }
    )
  } catch {
    // 用户关闭弹窗也执行刷新，保证尽快恢复可用
  } finally {
    window.location.reload()
  }
}

// 抑制 ResizeObserver 警告（这是一个已知的浏览器问题，不影响功能）
const resizeObserverErrorHandler = (e) => {
  const errorMessage = e.message || e.error?.message || ''
  if (isChunkLoadError(errorMessage)) {
    e.preventDefault()
    e.stopImmediatePropagation()
    e.stopPropagation()
    promptChunkRefresh()
    return false
  }
  if (errorMessage.includes('ResizeObserver')) {
    e.preventDefault()
    e.stopImmediatePropagation()
    e.stopPropagation()
    return false
  }
}
window.addEventListener('error', resizeObserverErrorHandler, true)

// 同时捕获 unhandledrejection 事件
window.addEventListener('unhandledrejection', (e) => {
  const errorMessage = e.reason?.message || String(e.reason) || ''
  if (isChunkLoadError(errorMessage)) {
    e.preventDefault()
    e.stopImmediatePropagation()
    promptChunkRefresh()
    return
  }
  if (errorMessage.includes('ResizeObserver')) {
    e.preventDefault()
    e.stopImmediatePropagation()
  }
})

app.use(pinia)
app.use(router)
app.use(ElementPlus)

// 在应用挂载前初始化主题（dark / light）
initTheme()

// 应用启动时，如果有 token，尝试加载用户信息，并预取挑战列表以改善 LCP
const authStore = useAuthStore()
if (authStore.token) {
  authStore.getCurrentUser().catch(() => {
    // 如果加载失败，清除 token（生产环境不输出日志）
    if (process.env.NODE_ENV === 'development') {
      console.warn('加载用户信息失败，清除 token')
    }
  })
  startChallengesPrefetch()
  startKbPrefetch()
}

// 初始化主题切换装扮的解锁状态（与主题本身解耦）
const themeStore = useThemeStore()
themeStore.initUnlock()

// 按需注册项目实际使用到的 Element Plus 图标
const icons = {
  User,
  Medal,
  ChatDotRound,
  Trophy,
  DataLine,
  ShoppingBag,
  Document,
  ChatLineRound,
  Setting,
  SwitchButton,
  ArrowDown,
  Bell,
  MoreFilled,
  Loading,
  Link,
  ArrowUp,
  InfoFilled,
  Clock,
  ArrowLeft,
  ArrowRight,
  Wallet,
  Plus,
  Upload,
  Delete,
  Edit,
  CircleCheck,
  CircleClose,
  StarFilled,
  Picture,
  List,
  PriceTag,
  FolderOpened,
  Operation,
  Collection,
  UploadFilled,
  Check,
  Search,
  Folder,
  Star,
  Monitor,
  Cpu,
  Connection,
  Tools,
  Refresh,
  Warning,
  QuestionFilled,
  Share
}

for (const [name, component] of Object.entries(icons)) {
  app.component(name, component)
}

app.mount('#app')
